import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { AuthService } from './auth.service';
import { UserType } from '../models/role.model';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * User type-based route guard
 * Checks if the current user has one of the allowed user types
 * User type determines the domain/layout for routing (owner, agency, employer, maid)
 *
 * Usage:
 *   canActivate: [userTypeGuard('owner', 'agency')]
 */
export function userTypeGuard(...allowedTypes: UserType[]): CanActivateFn {
    return (): Observable<boolean | UrlTree> => {
        const authService = inject(AuthService);
        const router = inject(Router);

        // If user data is ready, check user type immediately
        const currentUserType = authService.currentUserType();
        if (currentUserType) {
            return allowedTypes.includes(currentUserType)
                ? of(true)
                : of(router.createUrlTree(['/unauthorized']));
        }

        // No user data - try to fetch it
        if (authService.hasToken()) {
            return authService.fetchUserData().pipe(
                map(() => {
                    const userType = authService.currentUserType();
                    return userType && allowedTypes.includes(userType)
                        ? true
                        : router.createUrlTree(['/unauthorized']);
                }),
                catchError(() => {
                    authService.logout();
                    return of(router.createUrlTree(['/auth']));
                })
            );
        }

        // No token - redirect to login (unauthenticated)
        return of(router.createUrlTree(['/auth']));
    };
}